import { useEffect, useState } from 'react';
import { errorApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { createPermission } from '@backstage/plugin-permission-common';

export const HOSTED_APPLICATIONS_PLUGIN_ID = 'hosted-applications';

// Kept in sync with HOSTED_APP_PERMISSION_PREFIX in
// plugins/hosted-applications-backend/src/hostedApps.ts and
// packages/backend/src/extensions/ElitePermissionPolicy.ts.
const HOSTED_APP_PERMISSION_PREFIX = 'hosted-app.access.';
const SUBMENU_ANNOTATION = 'elite.io/hosted-app-submenu';
const DEFAULT_SUBMENU = 'Applications';

export type HostedApp = {
  uid: string;
  title: string;
  submenu: string;
};

export function hostedAppPath(appId: string): string {
  return `/apps/${encodeURIComponent(appId)}`;
}

export function getHostedAppPermission(appId: string) {
  return createPermission({
    name: HOSTED_APP_PERMISSION_PREFIX + appId,
    attributes: { action: 'read' },
  });
}

/**
 * Discovers hosted-application catalog entities, grouped by their submenu
 * annotation. Returns every candidate regardless of the current user's
 * access -- per-app access is checked separately via usePermission at render
 * time (see ExternalPageSubmenuGroup), which revalidates on focus/reconnect,
 * so a one-shot filter here would go stale when group membership changes.
 */
export function useHostedAppGroups(): Map<string, HostedApp[]> {
  const catalogApi = useApi(catalogApiRef);
  const errorApi = useApi(errorApiRef);
  const [groups, setGroups] = useState<Map<string, HostedApp[]>>(
    () => new Map(),
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { items } = await catalogApi.getEntities({
          filter: { kind: 'Resource', 'spec.type': 'hosted-application' },
          fields: [
            'metadata.uid',
            'metadata.name',
            'metadata.title',
            'metadata.annotations',
          ],
        });

        const next = new Map<string, HostedApp[]>();
        for (const entity of items) {
          if (!entity.metadata.uid) continue;
          const app: HostedApp = {
            uid: entity.metadata.uid,
            title: entity.metadata.title ?? entity.metadata.name,
            submenu:
              entity.metadata.annotations?.[SUBMENU_ANNOTATION] ??
              DEFAULT_SUBMENU,
          };
          if (!next.has(app.submenu)) next.set(app.submenu, []);
          next.get(app.submenu)!.push(app);
        }

        if (!cancelled) setGroups(next);
      } catch (error) {
        // Surface the failure instead of letting it become an unhandled
        // rejection; groups stays as it was, so the sidebar omits hosted apps.
        if (!cancelled) errorApi.post(error as Error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [catalogApi, errorApi]);

  return groups;
}
